import {BitmapText} from "pixi.js";
import {DrawFrmApp} from "../drawfrm/DrawFrmApp";
import {EventEmitter2} from "../util/EventEmitter2";
import {inches} from "../const";

// the clear space the label needs between its text and the lite's edges.
const FIT_PADDING = 0.5 * inches;

// the on-canvas glass size label, e.g. `GL 11 7/8 x 11 7/8`. Shared by the door lite
// and the frame lite so both views read the same.
export function glassLabelText(app: DrawFrmApp, size: {width: number; height: number}): string {
    return `GL ${app.formatDimension(size.width)} x ${app.formatDimension(size.height)}`;
}

// the y of each horizontal dimension line and the x of each vertical one, in the
// coords of the rect passed to fitGlassLabel.
export type DimensionLines = {horizontal: number[]; vertical: number[]};

// centre the label in the lite at full size, running along the lite's longer axis: a
// tall, narrow lite reads it vertically (like its height dimension), a wide one
// horizontally. Set the label's text first. Hides the label when it would overlap the
// lite's edges or a dimension line drawn over the lite.
export function fitGlassLabel(
    label: BitmapText,
    rect: {x: number; y: number; width: number; height: number},
    dimLines: DimensionLines,
) {
    const vertical = rect.height > rect.width;
    const halfX = (vertical ? label.height : label.width) / 2 + FIT_PADDING;
    const halfY = (vertical ? label.width : label.height) / 2 + FIT_PADDING;
    const cx = rect.x + rect.width / 2;
    const cy = rect.y + rect.height / 2;

    // the dimension text is centred on its line, so it reaches half a text height past it.
    const reach = label.height / 2;
    const crosses = (line: number, centre: number, half: number) =>
        line + reach > centre - half && line - reach < centre + half;

    if(cx - halfX < rect.x || cx + halfX > rect.x + rect.width
        || cy - halfY < rect.y || cy + halfY > rect.y + rect.height
        || dimLines.horizontal.some(y => crosses(y, cy, halfY))
        || dimLines.vertical.some(x => crosses(x, cx, halfX))) {
        label.visible = false;
        return;
    }

    label.visible = true;
    label.rotation = vertical ? -Math.PI / 2 : 0;
    label.position.set(cx, cy);
}

// the glass labels of the selected lites, keyed by lite id. The StatusBar shows them.
export class SelectedGlassLabels {
    labels = new EventEmitter2<Map<string, string>>(new Map(), "selectedGlassLabels");

    set(id: string, text: string) {
        if(this.labels.value.get(id) === text) return;
        const next = new Map(this.labels.value);
        next.set(id, text);
        this.labels.emit(next);
    }

    clear(id: string) {
        if(!this.labels.value.has(id)) return;
        const next = new Map(this.labels.value);
        next.delete(id);
        this.labels.emit(next);
    }
}
